import React, { lazy, useState, useEffect, Suspense, useCallback } from 'react';
import { TileLayout } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { ErrorBoundary } from 'react-error-boundary';
import { Link } from 'react-router-dom';
import { orderBy } from '@progress/kendo-data-query';
import { GlobalContext, ErrorFallback, Loader } from 'smart-react';
import {
  getLayout,
  handleReposition,
  saveLayout,
} from '../../../Utils/Layouts/tileLayoutHandler';
import './../../../assets/scss/common/Dashboard.scss';
import './Dashboard.scss';
import { DASHBOARD_TILELAYOUT } from '../../../constants/applicationConstants';
import AffectedFilesByRepos from '../Components/Cards/AffectedFilesByRepos';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { DEVICE_TYPES } from '../../../constants/eventDataTypes';
import { useAnalyticsData } from '../Services/DashboardQueries';

const RolloutsByProjectCard = lazy(
  () => import('../Components/Cards/RolloutsByProjectCard')
);
const RolloutActivities = lazy(
  () => import('../Components/Cards/RolloutActivities')
);
const DashboardCountCard = lazy(
  () => import('../../Core/Components/Cards/DashboardCountCard')
);
const BugsinRolloutCard = lazy(
  () => import('../Components/Cards/BugsinRolloutCard')
);

/**
 * Rollout Dashboard main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Rollout Dashboard component.
 */
const Dashboard = () => {
  const { deviceInfo } = React.useContext(GlobalContext);
  const { data: metrics, isLoading, isError } = useAnalyticsData();
  const tilelayout = DASHBOARD_TILELAYOUT;

  /**
   * Default Date Range to 1 week
   * @returns
   */
  const getDefaultDateRange = () => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    return {
      startDate: lastWeek.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const [dates, setDates] = useState(getDefaultDateRange());

  /**
   * Handle Date Change Event
   * @param {*} e
   * @param {*} field
   */
  const handleChange = (e, field) => {
    setDates({ ...dates, [field]: e.value });
    updateSelectedDate({ ...dates, [field]: e.value }, selectedRange);
  };

  /**
   * Define Layout for Desktop, Tablet, Mobile
   */
  const homeDefaultLayout = {
    LargeScreenLayout: [
      {
        col: 0,
        colSpan: 1,
        rowSpan: 1,
      },
    ],
    TabletScreenLayout: [
      {
        col: 0,
        colSpan: 1,
        rowSpan: 1,
      },
    ],
    MobileScreenLayout: [
      {
        col: 0,
        colSpan: 2,
        rowSpan: 1,
      },
    ],
  };

  const [allowReposition, setAllowReposition] = useState(false);

  const tiles = [
    {
      item: (
        <>
          {isLoading && <Loader />}
          <Link className="dashboard-action" to={`/projects`}>
            <DashboardCountCard
              cardClass={'blue-card'}
              label={'Total Rollouts'}
              value={metrics?.totalRollouts}
              iconClass={'blue'}
              icon={'sis-ico-list-board'}
            />
          </Link>
        </>
      ),
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'TotalRollouts',
      id: 1,
      layout: {
        LargeScreenLayout: [
          {
            col: 0,
            colSpan: 1,
            rowSpan: 1,
          },
        ],
        TabletScreenLayout: [
          {
            col: 0,
            colSpan: 2,
            rowSpan: 1,
          },
        ],
        MobileScreenLayout: [
          {
            col: 0,
            colSpan: 4,
            rowSpan: 1,
          },
        ],
      },
    },
    {
      item: (
        <>
          {isLoading && <Loader />}
          <Link className="dashboard-action" to={`/projects`}>
            <DashboardCountCard
              cardClass={'amber-card'}
              label={'Total Files Rolled Out '}
              value={metrics?.totalfiles}
              iconClass={'amber'}
              icon={'sis-ico-add-files'}
            />
          </Link>
        </>
      ),
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'TotalFiles',
      id: 2,
      layout: {
        LargeScreenLayout: [
          {
            col: 0,
            colSpan: 1,
            rowSpan: 1,
          },
        ],
        TabletScreenLayout: [
          {
            col: 0,
            colSpan: 2,
            rowSpan: 1,
          },
        ],
        MobileScreenLayout: [
          {
            col: 0,
            colSpan: 4,
            rowSpan: 1,
          },
        ],
      },
    },
    {
      item: (
        <>
          {isLoading && <Loader />}
          <Link className="dashboard-action" to={`/projects`}>
            <DashboardCountCard
              cardClass={'green-card'}
              label={'Unique Issues'}
              value={metrics?.uniqueIssues}
              iconClass={'green'}
              icon={'sis-ico-list-board-green'}
            />
          </Link>
        </>
      ),
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'UniqueIssues',
      id: 3,
      layout: {
        LargeScreenLayout: [
          {
            col: 0,
            colSpan: 1,
            rowSpan: 1,
          },
        ],
        TabletScreenLayout: [
          {
            col: 0,
            colSpan: 2,
            rowSpan: 1,
          },
        ],
        MobileScreenLayout: [
          {
            col: 0,
            colSpan: 4,
            rowSpan: 1,
          },
        ],
      },
    },
    {
      item: (
        <>
          {isLoading && <Loader />}
          <Link className="dashboard-action" to={`/projects`}>
            <DashboardCountCard
              cardClass={'yellow-card'}
              label={'Total Projects'}
              value={metrics?.totalprojects}
              iconClass={'yellow'}
              icon={'sis-ico-info-commands-yellow'}
            />
          </Link>
        </>
      ),
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'TotalProjects',
      id: 4,
      layout: {
        LargeScreenLayout: [
          {
            col: 0,
            colSpan: 1,
            rowSpan: 1,
          },
        ],
        TabletScreenLayout: [
          {
            col: 0,
            colSpan: 2,
            rowSpan: 1,
          },
        ],
        MobileScreenLayout: [
          {
            col: 0,
            colSpan: 4,
            rowSpan: 1,
          },
        ],
      },
    },
    {
      item: (
        <>
          {isLoading && <Loader />}
          <Link className="dashboard-action" to={`/projects`}>
            <DashboardCountCard
              cardClass={'purple-card'}
              label={'Total Repos'}
              value={metrics?.totalRepos}
              iconClass={'purple'}
              icon={'sis-ico-device-enrollments'}
            />
          </Link>
        </>
      ),
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'TotalVersions',
      id: 5,
      layout: {
        LargeScreenLayout: [
          {
            col: 0,
            colSpan: 1,
            rowSpan: 1,
          },
        ],
        TabletScreenLayout: [
          {
            col: 0,
            colSpan: 2,
            rowSpan: 1,
          },
        ],
        MobileScreenLayout: [
          {
            col: 0,
            colSpan: 4,
            rowSpan: 1,
          },
        ],
      },
    },
    {
      header: (
        <div className="card-header">
          <Link className="dashboard-action" to={`/projects`}>
            <span className="k-card-title">Rollouts by Project</span>
          </Link>
        </div>
      ),
      body: <RolloutsByProjectCard dates={dates} />,
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'RolloutByProjects',
      id: 6,
      layout: {
        LargeScreenLayout: [
          {
            colSpan: 2,
            rowSpan: 3,
          },
        ],
        TabletScreenLayout: [
          {
            colSpan: 2,
            rowSpan: 3,
          },
        ],
        MobileScreenLayout: [
          {
            colSpan: 4,
            rowSpan: 3,
          },
        ],
      },
    },
    {
      header: (
        <div className="card-header">
          <Link className="dashboard-action" to={`/projects`}>
            <span className="k-card-title">Rollout Activities</span>
          </Link>
        </div>
      ),
      body: <RolloutActivities dates={dates} />,
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'RolloutActivites',
      layout: {
        LargeScreenLayout: [
          {
            colSpan: 3,
            rowSpan: 3,
          },
        ],
        TabletScreenLayout: [
          {
            colSpan: 2,
            rowSpan: 3,
          },
        ],
        MobileScreenLayout: [
          {
            colSpan: 4,
            rowSpan: 3,
          },
        ],
      },
      id: 7,
    },
    {
      header: (
        <div className="card-header">
          <Link className="dashboard-action" to={`/projects`}>
            <span className="k-card-title">Affected Files By Repos</span>
          </Link>
        </div>
      ),
      body: <AffectedFilesByRepos dates={dates} />,
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'AffectedByRepos',
      layout: {
        LargeScreenLayout: [
          {
            colSpan: 2,
            rowSpan: 3,
          },
        ],
        TabletScreenLayout: [
          {
            colSpan: 2,
            rowSpan: 3,
          },
        ],
        MobileScreenLayout: [
          {
            colSpan: 4,
            rowSpan: 3,
          },
        ],
      },
      id: 8,
    },
    {
      header: (
        <div className="card-header">
          <Link className="dashboard-action" to={`/projects`}>
            <span className="k-card-title">Bugs In Rollout</span>
          </Link>
        </div>
      ),
      body: <BugsinRolloutCard dates={dates} />,
      resizable: allowReposition,
      reorderable: allowReposition,
      name: 'BugsinRollout',
      layout: {
        LargeScreenLayout: [
          {
            colSpan: 3,
            rowSpan: 3,
          },
        ],
        TabletScreenLayout: [
          {
            colSpan: 2,
            rowSpan: 3,
          },
        ],
        MobileScreenLayout: [
          {
            colSpan: 4,
            rowSpan: 3,
          },
        ],
      },
      id: 8,
    },
  ];

  /**
   * get Tile Layout Data
   * if data length is less then tiles length
   * it will duplicate data to match no of tiles.
   * @returns {Array} layoutData
   */
  const getDefaultLayout = (tilesData, layout) => {
    const filteredTiles = orderBy(tilesData ?? [], [
      { dir: 'asc', field: 'id' },
    ]);
    const layoutData = getLayout({
      tiles: filteredTiles, // an array of tile objects with ids
      tilelayout,
      deviceInfo,
      layout: layout,
    });

    // Ensure each tile gets its own layout, rather than filling with the same layout
    if (layoutData.length < tilesData.length) {
      return filteredTiles.map(
        (tile) => layoutData.find((l) => l.id === tile.id) || {}
      );
    }
    return layoutData;
  };

  const [data, setData] = useState(getDefaultLayout(tiles, homeDefaultLayout));

  useEffect(() => {
    setData(getDefaultLayout(tiles, homeDefaultLayout));
  }, [deviceInfo]);

  //Default Date option
  const dateRangeOptions = [
    { text: 'Last 7 Days', value: '7' },
    { text: 'Last 14 Days', value: '14' },
    { text: 'Last 30 Days', value: '30' },
    { text: 'Custom Date', value: 'custom' },
  ];

  const [selectedRange, setSelectedRange] = useState('7');

  const [showCalendar, setShowCalendar] = useState(false);

  /**
   * Handle Date Range Event
   * @param {*} e
   * @returns
   */
  const handleRangeChange = (e) => {
    const value = e.target.value;
    setSelectedRange(value?.value);

    // Handle predefined date range selection
    const today = new Date();
    let startDate;
    switch (value?.value) {
      case '7':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        setShowCalendar(false);
        break;
      case '14':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 14);
        setShowCalendar(false);
        break;
      case '30':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        setShowCalendar(false);

        break;
      case 'custom':
        setShowCalendar(true);
        return;
      default:
        return;
    }

    // Set dates for predefined ranges
    let dates = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
    setDates(dates);
    updateSelectedDate(dates, value?.value);
  };

  /**
   * Set Selected Date
   * @param {*} value
   */
  const updateSelectedDate = (value, range) => {
    localStorage.setItem(
      `${DASHBOARD_TILELAYOUT}_DATE_FILTER`,
      JSON.stringify({ range, dates: value })
    );
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className="home-page">
        <Suspense fallback={<Loader />}>
          <section className="tile-header">
            <div className="date-selector">
              <DropDownList
                className="date-range-selector k-mr-2"
                data={dateRangeOptions}
                value={dateRangeOptions.find(
                  (option) => option.value === selectedRange
                )}
                textField="text"
                dataItemKey="value"
                onChange={handleRangeChange}
              />
              {showCalendar && (
                <>
                  <div className="k-px-2">
                    <label>Start Date:</label>
                    <DatePicker
                      value={new Date(dates.startDate)}
                      format={'MM-dd-yyyy'}
                      onChange={(e) => handleChange(e, 'startDate')}
                    />
                  </div>
                  <div className="k-px-2">
                    <label>End Date:</label>
                    <DatePicker
                      value={new Date(dates.endDate)}
                      format={'MM-dd-yyyy'}
                      onChange={(e) => handleChange(e, 'endDate')}
                    />
                  </div>
                </>
              )}
              <Button
                className={`action-button`}
                icon={'refresh'}
                onClick={() => {
                  setDates({ ...dates });
                }}
                fillMode="outline"
                themeColor="primary"
              />
            </div>
            <Button
              fillMode={!allowReposition ? 'flat' : 'outline'}
              icon={!allowReposition ? 'edit' : ''}
              themeColor={'primary'}
              className=" edit-tiles"
              onClick={() =>
                saveLayout({
                  tilelayout,
                  data,
                  allowReposition,
                  setAllowReposition,
                  deviceInfo,
                })
              }
            >
              {!allowReposition ? '' : 'Done Editing'}
            </Button>
          </section>
          <TileLayout
            rowHeight={170}
            columns={deviceInfo?.type === DEVICE_TYPES.PHONE ? 4 : 5}
            positions={data}
            gap={{
              rows: 10,
              columns: 10,
            }}
            items={tiles}
            className="charts"
            onReposition={(e) => handleReposition(e, setData)}
          />
        </Suspense>
      </div>
    </ErrorBoundary>
  );
};

export default Dashboard;
